import { useEffect } from "react";
import moment from "moment";
import {
    BrightSpeedUserUtilsSlice as BrightSpeedUserUtilsType,
    setAccountType,
    setBillingType,
    setAccountTenure,
    setIsUserHaveAutoPay,
    setIsUserHavePaperlessBilling,
    setCurrentAmountDue,
    setBillDueDate,
    setLastInvoiceAmount,
    setLastBillDate,
    setInternetPlanSpeed,
    setVoice,
    setUserStateCode,
    setUserWireCenter,
    setIsCTLPrepayStatus,
    setIsCTLPostPayStatus,
    setIs4gltePostpay,
    setIsCTLMigratedPrepay,
    setIsCTLMigratedPostpay,
    setIsQF,
    setIsQF_II,
    setIsBRSPD,
    setIsPostPaidFiberUser,
    setIsPostPaidFiberUserHasBanner,
    setCanGTM_Tag_Trigger,
    setAccountStatus,
    setIsUserAccountStatus,
    setUserDetailsSection,
    setDslMigratorEligible,
    setDslMigrationPending,
    setDslMigrationCompleted,
    setDslMigrationCompletedBanner,
    setDslMigrationPendingApiStatus,
} from "../SharedContextStore/slices/brightSpeedUserUtils";
import { useAppDispatch, useAppSelector } from ".";
import { getFormatedStringFromDays, getServiceAddressFromUser } from "../Utils/utils";
import useBrightspeedHeaders from "./useBrightspeedHeader";
import { EC_DW_LOGGING_STEPS } from "../../../shared/utils/Constants";
import DwLogutil from "../Utils/DwLogutil";
import { setMigrationDslEligible, setMigrationNoVoiceEligible, setMigrationVoiceEligible } from "../SharedContextStore/slices/user";

export default function useBrightspeedUser(): BrightSpeedUserUtilsType {
    const headers = useBrightspeedHeaders();
    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state?.user?.data);
    const userData = useAppSelector((state) => state.user);
    const {companyName,sessionId} = userData;
    const userDataLoading = useAppSelector((state) => state?.user?.loading);
    const { planDetails: subscriptionPlanDetails, loading: planDetailsLoading } = useAppSelector(
        (state) => state.planDetails
    );
    const { upcomingAppointment } = useAppSelector((state) => state?.upComingAppointment);
    const userCompanyName = useAppSelector((state) => state.user.companyName);
     const {paymentMethods: listOfPaymentMethodRes,} = useAppSelector((state) => state?.paymentMethods);
    const {
        nextPaymentDetails,
        loading: nextPaymentDetailsLoading
    } = useAppSelector((state) => state.nextPaymentDetails);
    const { paymentHistory, loading: paymentHistoryLoading } = useAppSelector(
        (state) => state.paymentHistory
    );
    const { serviceDetails: servicePlan, loading: servicePlanDetailsLoading } = useAppSelector(
        (state) => state.serviceDetails
    );
    const wireCenter = useAppSelector(
        (state) => state?.addressQualification?.data?.addressQualification?.entity?.wireCenter
    );
      const instantInternet = useAppSelector(
        (state) => state?.addressQualification?.data?.addressQualification?.entity?.instantInternet
    );
    
    const addressQualificationLoading = useAppSelector((state) => state?.addressQualification?.loading);
    const brightSpeedUserUtils = useAppSelector((state) => state?.brightSpeedUserUtils);
    const GTM_Tag_Triggered_For = useAppSelector((state) => state?.brightSpeedUserUtils?.GTM_Tag_Triggered_For);
    const { ctlPlanDetails } = useAppSelector((state) => state?.planDetails);
    const addressQualification = useAppSelector((state) => state?.addressQualification?.data);
    const fibreQualified = addressQualification?.addressQualification?.entity?.brightSpeedFiberIndicator;
    const existingUser = addressQualification?.addressQualification?.entity?.existingService;
    const decomStatus = addressQualification?.addressQualification?.entity?.decomStatus;
    const isGrandFatherAccount = decomStatus?.toUpperCase() === "GRANDFATHERED";
    const isDiscountinuedAccount = decomStatus?.toUpperCase() === "DISCONTINUED";
    const workingNetworkType = addressQualification?.addressQualification?.entity?.workingNetworkType?.[0]?.toUpperCase();
    const CompletedOrderStatus = (upcomingAppointment?.upcomingAppointments[0]?.orderDetails?.pastAppointment[0]?.orderStatus)?.toUpperCase()?.includes("COMPLETED")
    const DSLplan = (ctlPlanDetails?.[0]?.internet?.productIdDescription !== undefined)
    const potsPlan = (ctlPlanDetails?.[0]?.internet?.length === 0 && ctlPlanDetails?.[0]?.pots?.length > 0)
    const isSuspendedUser = user?.currentStatus?.toUpperCase() === "SUSPENDED" ? true : false;
    const iwpServiceExists = servicePlan?.iwpServiceExists || false;
    // User Data dependent hook where we will check only user detail and set
    useEffect(() => {
        if(user?.email || user?.billingAccountNumber) {
            if(user?.customerSince) {
                const daysDifference =  moment(new Date()).diff(new Date(user?.customerSince), "days");
                dispatch(setAccountTenure(getFormatedStringFromDays(daysDifference)));
            }

            if(user?.currentStatus) {
                dispatch(setAccountStatus(user?.currentStatus));
            }

            if(user?.billingType) {
                dispatch(setAccountType(user?.billingType?.toLowerCase()?.trim() === "postpaid" ? "POSTPAID" : "PREPAID"))
                const billingType = user?.billingType?.toLowerCase()?.trim();
                if (billingType === "prepaid") {
                    dispatch(setBillingType("prepaid"));
                }
                else if (billingType === "postpaid") {
                    dispatch(setBillingType("postpaid"));
                }
                else {
                    dispatch(setBillingType(""));
                }
            }

            if(user?.addresses) {
                const serviceAdd = getServiceAddressFromUser(user);
                if(serviceAdd?.state) {
                    dispatch(setUserStateCode(serviceAdd?.state));
                }
            }

        }
    }, [user]);

    useEffect(() => {
        const DECOMISSION_CHANGE = process.env.REACT_APP_DECOMISSION_CHANGE === "true" ? true : false;
        const isctlUser = headers?.siteId?.toLowerCase() === "ctl";
        if (!isctlUser) return;

        const planDetails = ctlPlanDetails?.[0];
        const dslInternetDesc = planDetails?.internet?.productIdDescription;
        const dslPotS =
            planDetails?.pots?.length > 0 &&
            Boolean(planDetails?.pots?.[0]?.phoneNo);
        const multiLinePots = planDetails?.pots?.length > 1 ? true : false;
        const DSLplanonly = Boolean(dslInternetDesc) && !dslPotS;
        const potsPlanonly = !dslInternetDesc && dslPotS;
        const DSLplanAndPotsPlan = Boolean(dslInternetDesc) && dslPotS;
        const fiberVoiceEligible = addressQualification?.addressQualification?.entity?.fiberVoiceEligible;
        const active_user = user?.currentStatus?.toUpperCase() === "ACTIVE";
        const isUserOnJeopardy = subscriptionPlanDetails?.jeopardyFlag === "Y" ? true : false;

        // -------------------------
        // DSL / POTS COMBINATION LOGGING
        // -------------------------
        if (DSLplanonly) {
            DwLogutil({ stepName: EC_DW_LOGGING_STEPS.dsl_plan_only, email: user?.email });
        }

        if (potsPlanonly) {
            DwLogutil({ stepName: EC_DW_LOGGING_STEPS.pots_plan_only, email: user?.email });
            
            if (fiberVoiceEligible) {
                DwLogutil({
                    stepName: EC_DW_LOGGING_STEPS.pots_plan_only_with_voice,
                    email: user?.email
                });
            } else {
                DwLogutil({
                    stepName: EC_DW_LOGGING_STEPS.pots_plan_only_no_voice,
                    email: user?.email
                });
            }

        }

        if (DSLplanAndPotsPlan) {
            DwLogutil({ stepName: EC_DW_LOGGING_STEPS.dsl_pots_plan, email: user?.email });
           
            
            if (fiberVoiceEligible) {
                DwLogutil({
                    stepName: EC_DW_LOGGING_STEPS.dsl_pots_plan_with_voice,
                    email: user?.email
                });
            } else {
                DwLogutil({
                    stepName: EC_DW_LOGGING_STEPS.dsl_pots_plan_no_voice,
                    email: user?.email
                });
            }

        }

        // -------------------------
        // MIGRATION ELIGIBILITY
        // -------------------------

        // const brightSpeedFiberIndicator =
        //     addressQualification?.entity?.brightSpeedFiberIndicator;

        // const existingService =
        //     addressQualification?.entity?.existingService;

        // -------------------------
        // MIGRATION LOGGING + DISPATCH
        // -------------------------

        const copperuser_with_fiberQualified = isctlUser && (existingUser === true) && (fibreQualified?.toUpperCase() === "QUALIFIED") && (workingNetworkType === "COPPER");
        const active_copperuser_with_fiberQualified = copperuser_with_fiberQualified && active_user && CompletedOrderStatus;

       
        if(subscriptionPlanDetails && !isUserOnJeopardy && !isSuspendedUser && !iwpServiceExists && ctlPlanDetails?.length > 0) {            
        // Migration Eligible with DSL & POTS
        if (fibreQualified && existingUser && active_user && (DSLplanAndPotsPlan) && fiberVoiceEligible && !multiLinePots) {
            DwLogutil({ stepName:EC_DW_LOGGING_STEPS.migration_dsl, email: user?.email });
            dispatch(setMigrationDslEligible?.(true)); 
        }

        // Migration Eligible with Voice(POTS only)
        if (fibreQualified && existingUser && fiberVoiceEligible && active_user && (potsPlanonly) && !multiLinePots) {
            DwLogutil({ stepName:EC_DW_LOGGING_STEPS.migration_voice, email: user?.email });
            dispatch(setMigrationVoiceEligible?.(true)); 
        }

        // Migration Eligible with No Voice(DSL only)
        if (fibreQualified && existingUser && !fiberVoiceEligible && active_user && (DSLplanonly)) {
            DwLogutil({ stepName:EC_DW_LOGGING_STEPS.migration_no_voice, email: user?.email });
            dispatch(setMigrationNoVoiceEligible?.(true)); 
        }

        // Flag to identify the eligible user for seamless migration
        // need to remove this while decomission deployement
        if((!DECOMISSION_CHANGE && active_copperuser_with_fiberQualified && !multiLinePots && !(brightSpeedUserUtils?.dslMigrationPending) &&  fiberVoiceEligible && (DSLplan || DSLplanAndPotsPlan || potsPlanonly)) || (!fiberVoiceEligible && DSLplanonly && active_copperuser_with_fiberQualified)) {
            dispatch(setDslMigratorEligible(true));
            DwLogutil({ stepName:EC_DW_LOGGING_STEPS.seamless_migration_eligible});
        }
       // need to remove this while decomission deployement

       
        if ((DECOMISSION_CHANGE && active_copperuser_with_fiberQualified &&
            !multiLinePots &&
            !(brightSpeedUserUtils?.dslMigrationPending) &&
            fiberVoiceEligible &&
            (DSLplan || DSLplanAndPotsPlan || potsPlanonly) && !((potsPlanonly || DSLplanAndPotsPlan) && (isGrandFatherAccount || isDiscountinuedAccount))
        ) ||
            (!fiberVoiceEligible && DSLplanonly && active_copperuser_with_fiberQualified)) {
            dispatch(setDslMigratorEligible(true));
            DwLogutil({ stepName:EC_DW_LOGGING_STEPS.seamless_migration_eligible});
        }
    }

    }, [
        ctlPlanDetails,
        addressQualification,
        subscriptionPlanDetails,
        upcomingAppointment 
    ]);

    useEffect(() => {
        const pendingOrderDetails = upcomingAppointment?.upcomingAppointments[0]?.orderDetails?.pendingOrderDetails;
        const serviceDetails = servicePlan?.subscriptions?.[0]?.serviceDetails ?? [];

        // removed this check to get allign with mobile
        const migrationPending = pendingOrderDetails?.some((item: { orderType: string; orderStatus: string; }) =>
            item.orderType?.toUpperCase() === "CHANGE" &&
            item.orderStatus?.toUpperCase() !== "COMPLETED"
        );
        const installtionPendingforUpgrade = serviceDetails?.filter((item: any) => item.isParent?.toUpperCase() === "TRUE" &&
            ["FIBER INTERNET", "FIBER VOICE"].includes(item?.serviceType?.toUpperCase()) && item?.serviceState?.toUpperCase() === "INSTALLATION PENDING");

        const isActivationPending = serviceDetails?.filter((item: any) => item.isParent?.toUpperCase() === "TRUE" &&
            ["COPPER DSL", "COPPER POTS"].includes(item?.serviceType?.toUpperCase()) && item?.serviceState?.toUpperCase() === "INACTIVATION PENDING");

        if (installtionPendingforUpgrade?.length > 0 && isActivationPending?.length > 0) {
            dispatch(setDslMigrationPendingApiStatus(false));
            dispatch(setDslMigrationPending(true));
        }
        // removed this check to get allign with mobile
        const migrationCompleted = pendingOrderDetails?.some((item: { orderType: string; orderStatus: string; }) =>
            item.orderType?.toUpperCase() === "CHANGE" &&
            item.orderStatus?.toUpperCase() === "COMPLETED"
        );
        const installtionCompletedforUpgrade = serviceDetails?.filter((item: any) => item.isParent?.toUpperCase() === "TRUE" &&
            ["FIBER INTERNET", "FIBER VOICE"].includes(item?.serviceType?.toUpperCase()) && item?.serviceState?.toUpperCase() === "ACTIVE");

        const isInActivationCopper = serviceDetails?.filter((item: any) => item.isParent?.toUpperCase() === "TRUE" &&
            ["COPPER DSL", "COPPER POTS"].includes(item?.serviceType?.toUpperCase()) && item?.serviceState?.toUpperCase() === "INACTIVE");

        if (installtionCompletedforUpgrade?.length > 0 && isInActivationCopper?.length > 0) {
            localStorage.removeItem("dslMigrationPending");
            dispatch(setDslMigrationPendingApiStatus(false));
            dispatch(setDslMigrationCompleted(true));
        }
        const dueDateStr = nextPaymentDetails?.dueDate;
        const invoiceGenerated = nextPaymentDetails?.invoiceGenerated;
        const nextPayment = Number(nextPaymentDetails?.nextPayment);
        const validNextPayment = Number.isFinite(nextPayment) && nextPayment > 0;
        let isFirstBillCycleDone = false;
        if (dueDateStr) {
            const [month, day, year] = dueDateStr.split('/').map(Number);
            if (!isNaN(month) && !isNaN(day) && !isNaN(year)) {
                const dueDate = new Date(year, month - 1, day);
                dueDate.setHours(0, 0, 0, 0);

                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const isDueDateTodayOrPast = dueDate >= today;
                if (isDueDateTodayOrPast && invoiceGenerated == true && validNextPayment) {
                    isFirstBillCycleDone = true
                }

                if (installtionCompletedforUpgrade?.length > 0 && isInActivationCopper?.length > 0 && !isFirstBillCycleDone) {
                    dispatch(setDslMigrationCompletedBanner(true));
                }
            }
        }

    }, [upcomingAppointment, nextPaymentDetails])


    // subscription plan API dependent hook where we will check API response and update the state accordigly
    useEffect(() => {
        if(user?.billingType?.toLowerCase()?.trim() === "postpaid" && subscriptionPlanDetails?.autoPayIndicator?.toUpperCase()?.trim() === "ZY") {
            dispatch(setIsUserHaveAutoPay(true));
        }
        else if(user?.billingType?.toLowerCase()?.trim() === "postpaid" && subscriptionPlanDetails?.autoPayIndicator?.toUpperCase()?.trim() === "ZN") {
            dispatch(setIsUserHaveAutoPay(false));
        }
        
        if(user?.billingType?.toLowerCase()?.trim() === "postpaid"&& subscriptionPlanDetails?.paperlessBilling?.toUpperCase()?.trim() === "ZY") {
            dispatch(setIsUserHavePaperlessBilling(true));
        }

        else if(user?.billingType?.toLowerCase()?.trim() === "postpaid"&& subscriptionPlanDetails?.paperlessBilling?.toUpperCase()?.trim() === "ZN") {
            dispatch(setIsUserHavePaperlessBilling(false));
        }

    }, [subscriptionPlanDetails])




    // upcoming payment detail API dependent hook where we will check API response and update the state accordigly
    useEffect(() => {
        if(nextPaymentDetails?.nextPayment) {
            dispatch(setCurrentAmountDue(nextPaymentDetails?.nextPayment))
        }
        if(nextPaymentDetails?.dueDate) {
            dispatch(setBillDueDate(nextPaymentDetails?.dueDate));
        }
    }, [nextPaymentDetails])
    
    // payment history API dependent hook where we will check API response and update the state accordigly
    useEffect(() => {
        if(paymentHistory?.payments?.length > 0) {
            const sortedPaymentHistory = paymentHistory?.payments?.slice()?.sort((a: any, b: any) => {
                return new Date(b?.effectiveDate)?.valueOf() - new Date(a?.effectiveDate)?.valueOf()
            });
            
            if(sortedPaymentHistory?.[0]?.effectiveDate) {
                dispatch(setLastBillDate(sortedPaymentHistory[0]?.effectiveDate));
            }
            if(sortedPaymentHistory?.[0]?.amount) {
                dispatch(setLastInvoiceAmount(sortedPaymentHistory[0]?.amount));
            }
        }
    }, [paymentHistory]);
    
    // service details API dependent hook where we will check API response and update the state accordigly
    useEffect(() => {
        if(servicePlan?.subscriptions?.length > 0) {
            for (let index = 0; index < servicePlan?.subscriptions[0]?.serviceDetails.length; index++) {
                const element = servicePlan?.subscriptions[0]?.serviceDetails[index];
                if(((headers?.siteId?.toUpperCase() === "CTL" && element?.isParent?.toUpperCase() === "TRUE") || headers?.siteId?.toUpperCase() !== "CTL") && 
                    element?.productType === "WA" && 
                    element?.serviceState === "Active") {
                        dispatch(setInternetPlanSpeed(element?.serviceName || element?.serviceLevel));
                } else if(headers?.siteId?.toUpperCase() !== "CTL" && (element?.serviceType?.includes("Internet") || element?.serviceName?.includes("Internet"))) {
                    dispatch(setInternetPlanSpeed(element?.serviceName || element?.serviceLevel))
                }

                if(element?.serviceState === "Active" && element?.serviceType?.toLowerCase()?.includes("voice")) {
                    dispatch(setVoice(element?.serviceName || element?.serviceType));
                } else if(((headers?.siteId?.toUpperCase() === "CTL" && element?.isParent?.toUpperCase() === "TRUE") || headers?.siteId?.toUpperCase() !== "CTL") && 
                    element?.productType === "IL" && 
                    element?.serviceState === "Active"){
                    dispatch(setVoice(element?.serviceName || element?.serviceType));
                }
            }
        }
    }, [servicePlan]);

    // address Qualification API dependent hook where we will check API response and update the state accordigly
    useEffect(() => {
        if(wireCenter) {
            dispatch(setUserWireCenter(wireCenter));
        }
    }, [wireCenter]);

    useEffect(() => {   
        
        //DAY-3425- Added the condition for the multiaccounts can have combination of 
        // (copper and fiber siteId based on the latest siteid we are updating the state  );
        const siteId = headers?.siteId;
        const isMigrated = user && user?.migratedAccount?.toUpperCase() === "Y" ? true : false;
        //Multiaccount switch billingType
        const isPostpaid = (user?.billingType && user?.billingType?.toLowerCase()?.trim() === "postpaid") ? true : false;
        const isCtl = siteId?.toLowerCase() === "ctl" ? true : false;
        const  serviceNetworkType = user?.addresses?.find(
            (addr:any) => addr.addressType === "ServiceAddress"
        )?.networkType
       
        const getCTLFlags = () => {
          return {
            isCtlPostpay: isCtl && isPostpaid,
            isCtlMigratedPostpay: isCtl && isPostpaid && isMigrated,
            isCtlPrepay: isCtl && !isPostpaid,
            isCtlMigratedPrepay: isCtl && !isPostpaid && isMigrated,
          };
        };
      
        const getSiteFlags = () => ({
          is4GLte: siteId?.toLowerCase() === "4glte",
          isBSUser: serviceNetworkType?.toLowerCase() === "bs" || serviceNetworkType?.toLowerCase() === "xgspon" || siteId?.toLowerCase() === "bs",
          isQFUser: serviceNetworkType?.toLowerCase() === "qf" || serviceNetworkType?.toLowerCase() === "gpon" || siteId?.toLowerCase() === "qf",
          isInstantInternet: (instantInternet || siteId?.toLowerCase() === "qf-ii") ,
        });
       
        const {
          isCtlPostpay,
          isCtlMigratedPostpay,
          isCtlPrepay,
          isCtlMigratedPrepay,
        } = getCTLFlags();
       
        const {
          is4GLte,
          isBSUser,
          isQFUser,
          isInstantInternet,
        } = getSiteFlags();
        const serviceDetails = servicePlan?.subscriptions?.[0]?.serviceDetails ?? [];


         const isPostPaidFiberUser = ((isPostpaid && (isBSUser || isQFUser)))  ? true : false
         //reverted the condition as it is not applicable for dsl migrated users
        const postPaidFiberHasBanner = () => {
            const ppFiberNewUser = isPostpaid && (isBSUser || isQFUser) && !isMigrated ? true : false
            const ppFiberExistingEligibleUser = isPostpaid && isBSUser ? true : false
            
            if(ppFiberNewUser || ppFiberExistingEligibleUser) return true;

            return false;
        }
        const isPostPaidFiberHasBanner = postPaidFiberHasBanner()

        /**
         * Billing type postpaid  - isPostpaid
            Billing system brim - billingSystem
            Migrated customer Y - isMigrated
            Network type. gPOn - isQFUser
         */

        dispatch(setIsCTLPostPayStatus(isCtlPostpay));
        dispatch(setIsCTLMigratedPostpay(isCtlMigratedPostpay));
        dispatch(setIsCTLPrepayStatus(isCtlPrepay));
        dispatch(setIsCTLMigratedPrepay(isCtlMigratedPrepay));
        dispatch(setIsBRSPD(isBSUser));
        dispatch(setIs4gltePostpay(is4GLte));
        dispatch(setIsQF(isQFUser));
        dispatch(setIsQF_II(isInstantInternet));
        dispatch(setIsPostPaidFiberUser(isPostPaidFiberUser));
        dispatch(setIsPostPaidFiberUserHasBanner(isPostPaidFiberHasBanner));
      }, [headers, user,servicePlan]);

    useEffect(() => {
        if(!userDataLoading && 
            !planDetailsLoading && 
            !nextPaymentDetailsLoading && 
            !paymentHistoryLoading &&
            !servicePlanDetailsLoading &&
            !addressQualificationLoading &&
            !planDetailsLoading &&
            subscriptionPlanDetails &&
            GTM_Tag_Triggered_For !== user?.billingAccountNumber
        ) {
            setTimeout(() => dispatch(setCanGTM_Tag_Trigger(true)), 8000);
        }
    }, [
        userDataLoading, planDetailsLoading, 
        nextPaymentDetailsLoading, paymentHistoryLoading,
        servicePlanDetailsLoading, addressQualificationLoading,
        GTM_Tag_Triggered_For, subscriptionPlanDetails
    ])

    const isMigrated = useAppSelector((state) => state?.isMigrated?.isMigrated);

    // const isUserAccountActive = ["ACTIVE"]?.includes(user?.currentStatus?.toUpperCase());
    const isUserAccountPaused = user?.currentStatus?.toUpperCase() === "PAUSED";
    const isUserAccountPending = user?.currentStatus?.toUpperCase() === "PENDING";
    const isUserAccountActive = user?.currentStatus?.toUpperCase() === "ACTIVE";
    const invoiceGenerated = nextPaymentDetails?.invoiceGenerated;
    useEffect(()=>{
        const isOrderNotCompleted = upcomingAppointment?.length  && upcomingAppointment?.orderStatusForWallgarden?.toUpperCase() !== "COMPLETED"
        if (isUserAccountPending || (servicePlan !== null && servicePlan?.serviceIsInPending)) {
            dispatch(setIsUserAccountStatus("Pending installation"));
            return;
        }
    
        else if ((servicePlan?.pendingServiceChange !== undefined || (isOrderNotCompleted))) {
            dispatch(setIsUserAccountStatus("Changes pending"));
            return;
        }
        else if (isUserAccountPaused) {
            dispatch(setIsUserAccountStatus("Account Paused"));
            return;
        }
        else if ( /* headers?.siteId?.toUpperCase() === "CTL" && */ isUserAccountActive && invoiceGenerated === false) { // if invoice not generated, it is consider as billpending
            dispatch(setIsUserAccountStatus("Bill pending"));
            return;
        }
        else{
             dispatch(setIsUserAccountStatus(""));
            return;
        } 
    
}, [ user, nextPaymentDetails, servicePlan, upcomingAppointment, listOfPaymentMethodRes])


    return brightSpeedUserUtils;
}
